import { createAsyncThunk } from '@reduxjs/toolkit'

import {
  BLASTER_URL_PREFIX,
  defaultChannel,
  MIN_FOLLOWING_COUNTDOWN_SECONDS,
} from '../../constants/constants'
import { AppDispatch, RootState } from '../../reducers'
import blasterPeersSlice from '../../reducers/blasterPeersSlice'
import sessionSlice from '../../reducers/sessionSlice'
import { selectPeerChannel } from '../../selectors/blaster-peer-selectors'
import {
  selectCurrentPlaylistSlug,
  selectCurrentPlayStatus,
  selectCurrentTrackInfo,
} from '../../selectors/current-play-selectors'
import {
  selectCurrentMatchSlug,
  selectCurrentSessionInfo,
  selectCurrentUsername,
} from '../../selectors/session-selectors'
import { selectViewSettings } from '../../selectors/settings-selectors'
import realtime from '../../services/RealtimeService'
import trackMixer from '../../services/TrackMixer'
import { UserChannel } from '../../types'
import loadTrackFromUri from '../track-loading/loadTrackFromUri'

const updateUserStatus = createAsyncThunk<void, string, { state: RootState }>(
  'updateUserStatus',
  async (status, { getState }) => {
    const sessionStatus = {
      username: selectCurrentUsername(getState()),
      status,
      timestamp: Date.now(),
    }
    realtime.set('session/latest', sessionStatus)
  }
)

export const updateChannelTrack = createAsyncThunk<void, void, { state: RootState }>(
  'updateChannel',
  async (_, { dispatch, getState }) => {
    const state = getState()
    const currUsername = selectCurrentUsername(state)
    const channel = selectPeerChannel(currUsername)(state)
    if (!channel || !channel.isBroadcasting) {
      return
    }
    const matchSlug = selectCurrentMatchSlug(state)
    const playlistSlug = selectCurrentPlaylistSlug(state)
    const { slug: trackSlug, title, artist } = selectCurrentTrackInfo(state)
    const { isShowLyrics } = selectViewSettings(0)(state)
    const broadcastTrackSlug = `${matchSlug}/${playlistSlug}/${trackSlug}`
    const currentTrack = {
      slug: broadcastTrackSlug,
      title,
      artist,
    }
    const updatedChannel = {
      ...channel,
      currentTrack,
      isHideLyrics: !isShowLyrics,
      trackWillPlayAtTimestamp: 0,
    }
    // Object.keys(channel.listeners).forEach((key) => {
    //   updatedChannel.listeners[key].isSynced = false
    // })
    realtime.set(`channels/${currUsername}/default`, updatedChannel)
    dispatch(
      blasterPeersSlice.actions.updateChannel({ username: currUsername, channel: updatedChannel })
    )
  }
)

export const toggleChannelBroadcast = createAsyncThunk<void, void, { state: RootState }>(
  'updateChannel',
  async (_, { dispatch, getState }) => {
    const state = getState()
    const currUsername = selectCurrentUsername(state)
    const channelSlug = 'default'
    const existingChannel = selectPeerChannel(currUsername)(state)
    const matchSlug = selectCurrentMatchSlug(state)
    const playlistSlug = selectCurrentPlaylistSlug(state)
    const { slug: trackSlug, title, artist } = selectCurrentTrackInfo(state)
    const broadcastTrackSlug = `${matchSlug}/${playlistSlug}/${trackSlug}`
    const channel = existingChannel
      ? { ...existingChannel }
      : defaultChannel(currUsername, channelSlug)
    channel.isBroadcasting = !channel.isBroadcasting
    channel.currentTrack = {
      slug: broadcastTrackSlug,
      title,
      artist,
    }
    if (existingChannel) {
      realtime.set(`channels/${currUsername}/default/isBroadcasting`, channel.isBroadcasting)
      realtime.set(`channels/${currUsername}/default/currentTrack`, channel.currentTrack)
    } else {
      realtime.set(`channels/${currUsername}/default`, channel)
    }
    dispatch(blasterPeersSlice.actions.updateChannel({ username: currUsername, channel }))
  }
)

export const dropChannel = createAsyncThunk<void, string, { state: RootState }>(
  'dropChannel',
  async (compoundChannelSlug, { dispatch, getState }) => {
    const state = getState()
    const currUsername = selectCurrentUsername(getState())
    const ourChannel = selectPeerChannel(currUsername)(state)
    // TODO: keep local listeners state up to date?
    // const peerChannel = selectPeerChannel(peerName)(state)
    realtime.endpoint(`channels/${compoundChannelSlug}/`).off()
    realtime.set(`channels/${compoundChannelSlug}/listener/${currUsername}/`, null)
    realtime.set(`channels/${currUsername}/default/following/`, null)
    dispatch(
      blasterPeersSlice.actions.updateChannel({
        username: currUsername,
        channel: { ...ourChannel, following: '' },
      })
    )
  }
)

export const tuneToChannel = createAsyncThunk<
  void,
  string,
  { state: RootState; dispatch: AppDispatch }
>('tuneToChannel', async (peerName, { dispatch, getState }) => {
  const state = getState()
  const currUsername = selectCurrentUsername(state)
  const ourChannel = selectPeerChannel(currUsername)(state)
  const currentlyFollowing = ourChannel.following
  const peerChannel = `${peerName}/default`
  // if (currentlyFollowing === peerChannel) {
  //   return
  // }
  if (currentlyFollowing) {
    dispatch(dropChannel(currentlyFollowing)) // TODO: need to await this?
  }
  if (!peerName) {
    return
  }
  const defaultListenerStatus = { isSynced: false, isPlaying: false }
  realtime.set(`channels/${peerChannel}/listeners/${currUsername}/`, defaultListenerStatus)
  realtime.set(`channels/${currUsername}/default/following/`, peerChannel)
  dispatch(
    blasterPeersSlice.actions.updateChannel({
      username: currUsername,
      channel: { ...ourChannel, following: peerChannel },
    })
  )
  // TODO: keep local listeners state up to date?
  // channelStatus.listeners[currUsername] = defaultListenerStatus
  // dispatch(
  //   blasterPeersSlice.actions.updateChannel({ username: peerName, channel: channelStatus })
  // )
  realtime.endpoint(`channels/${peerName}/default`).on('value', (snapshot) => {
    const rawValue = snapshot.val()
    if (!rawValue) {
      return // channel does not exist
    }
    const currentTrackPath = getState().session.currentTrackPath
    const channelStatus = rawValue as UserChannel
    const {
      expiresAt = 0,
      slug,
      currentTrack,
      trackWillPlayAtTimestamp,
      isHideLyrics,
    } = channelStatus
    const isExpired = false && Date.now() > expiresAt
    if (isExpired) {
      dispatch(dropChannel(slug))
    } else {
      if (!channelStatus.listeners) {
        channelStatus.listeners = {}
      }
      if (currUsername in channelStatus.listeners) {
        trackMixer.toggleMasked(!isHideLyrics)
        const trackPath = `/${BLASTER_URL_PREFIX}/${currentTrack.slug}`
        if (trackPath !== currentTrackPath) {
          dispatch(loadTrackFromUri({ trackPath }))
        } else if (trackWillPlayAtTimestamp) {
          const countdownMillis = trackWillPlayAtTimestamp - Date.now()
          const haveMargin = countdownMillis >= 1000 * MIN_FOLLOWING_COUNTDOWN_SECONDS
          if (haveMargin) {
            const countdownSeconds = Math.floor(countdownMillis / 1000)
            const delay = countdownMillis - countdownSeconds * 1000
            console.log(`starting ${countdownSeconds}sec countdown in ${delay}ms`)
            setTimeout(() => {
              dispatch(sessionSlice.actions.setCountdownSeconds(countdownSeconds))
              dispatch(sessionSlice.actions.setIsShowCountdownClock(true))
            }, delay)
          } else {
            console.log(
              `ignoring play: ${countdownMillis} < ${1000 * MIN_FOLLOWING_COUNTDOWN_SECONDS}`
            )
          }
        }
      }
    }
  })
})

const songSwitched = createAsyncThunk<void, void, { state: RootState }>(
  'songSwitched',
  async (_, { dispatch, getState }) => {
    const isBroadcasting = true // TODO: allow setting, check if anyone listening?
    if (!isBroadcasting) {
      return
    }
    const state = getState()
    const { username, currentMatchSlug: matchSlug } = selectCurrentSessionInfo(state)
    const playlistSlug = selectCurrentPlaylistSlug(state)
    const playStatus = selectCurrentPlayStatus(0)(state)
    const messagePayload = {
      from: username,
      message: { matchSlug, playlistSlug, playStatus },
      timestamp: Date.now(),
    }
    realtime.set(`user/${username}/songSwitch`, messagePayload)
    // dispatch(updatePlayFollowers())
  }
)

export { songSwitched, updateUserStatus }
